import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-hot-toast';

import { updateTournament } from '../../../redux/slices/tournamentSlice';
import { updateOrganisation } from '../../../redux/slices/organisationSlice';

/**
 * SettingsV2 - Modern JSX component with Tailwind CSS
 * Comprehensive settings page for tournament and organization management
 * Allows users to update tournament details, organization info, and preferences
 */
const SettingsV2 = () => {
  const dispatch = useDispatch();
  const { selectedTournament } = useSelector((state) => state.tournament);
  const { selectedOrganisation } = useSelector((state) => state.organisation);
  const { user } = useSelector((state) => state.auth);

  const [activeTab, setActiveTab] = useState('tournament');
  const [loading, setLoading] = useState(false);

  // Tournament Settings State
  const [tournamentSettings, setTournamentSettings] = useState({
    name: '',
    description: '',
    startDate: '',
    endDate: '',
    location: '',
    rules: '',
  });

  // Organization Settings State
  const [orgSettings, setOrgSettings] = useState({
    name: '',
    email: '',
    phone: '',
    address: '',
    website: '',
  });

  // User Preferences State
  const [preferences, setPreferences] = useState({
    notifications: true,
    emailUpdates: true,
    darkMode: true,
  });

  // Load tournament settings
  useEffect(() => {
    if (selectedTournament) {
      setTournamentSettings({
        name: selectedTournament.name || '',
        description: selectedTournament.description || '',
        startDate: selectedTournament.startDate || '',
        endDate: selectedTournament.endDate || '',
        location: selectedTournament.location || '',
        rules: selectedTournament.rules || '',
      });
    }
  }, [selectedTournament]);

  // Load organization settings
  useEffect(() => {
    if (selectedOrganisation) {
      setOrgSettings({
        name: selectedOrganisation.name || '',
        email: selectedOrganisation.email || '',
        phone: selectedOrganisation.phone || '',
        address: selectedOrganisation.address || '',
        website: selectedOrganisation.website || '',
      });
    }
  }, [selectedOrganisation]);

  // Handle tournament settings change
  const handleTournamentChange = (e) => {
    const { name, value } = e.target;
    setTournamentSettings((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  // Handle organization settings change
  const handleOrgChange = (e) => {
    const { name, value } = e.target;
    setOrgSettings((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  // Handle preferences change
  const handlePreferenceChange = (e) => {
    const { name, checked } = e.target;
    setPreferences((prev) => ({
      ...prev,
      [name]: checked,
    }));
  };

  // Save tournament settings
  const handleSaveTournamentSettings = async () => {
    if (!selectedTournament) {
      toast.error('No tournament selected');
      return;
    }

    try {
      setLoading(true);
      await dispatch(
        updateTournament({
          id: selectedTournament.id || selectedTournament._id,
          updateData: tournamentSettings,
        })
      ).unwrap();
      toast.success('Tournament settings updated successfully');
    } catch (error) {
      toast.error(error.message || 'Failed to update tournament settings');
    } finally {
      setLoading(false);
    }
  };

  // Save organization settings
  const handleSaveOrgSettings = async () => {
    if (!selectedOrganisation) {
      toast.error('No organization selected');
      return;
    }

    try {
      setLoading(true);
      await dispatch(
        updateOrganisation({
          id: selectedOrganisation.id || selectedOrganisation._id,
          updateData: orgSettings,
        })
      ).unwrap();
      toast.success('Organization settings updated successfully');
    } catch (error) {
      toast.error(error.message || 'Failed to update organization settings');
    } finally {
      setLoading(false);
    }
  };

  // Save preferences
  const handleSavePreferences = () => {
    localStorage.setItem('userPreferences', JSON.stringify(preferences));
    toast.success('Preferences saved successfully');
  };

  const tabs = [
    { id: 'tournament', label: 'Tournament Settings' },
    { id: 'organization', label: 'Organization' },
    { id: 'preferences', label: 'Preferences' },
    { id: 'account', label: 'Account' },
  ];

  return (
    <div className="min-h-screen bg-[#000F1F] text-[#FEFCEB]">
      <div className="max-w-[1200px] mx-auto py-6 px-6">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-[#FEFCEB] m-0">Settings</h1>
          <p className="text-[#7F8585] mt-2">
            Manage your tournament, organization, and account settings
          </p>
        </div>

        {/* Tabs */}
        <div className="flex items-center gap-1 mb-8 border-b border-[#192634]">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`
                px-6 py-3 text-base font-medium transition-colors relative
                ${
                  activeTab === tab.id
                    ? 'text-[#00E7FF]'
                    : 'text-[#7F8585] hover:text-gray-200'
                }
              `}
            >
              {tab.label}
              {activeTab === tab.id && (
                <div className="absolute bottom-0 left-0 w-full h-[2px] bg-[#00E7FF]" />
              )}
            </button>
          ))}
        </div>

        {/* Content */}
        <div className="bg-[#011224] rounded-2xl border border-[#192634] p-6">
          {/* Tournament Settings Tab */}
          {activeTab === 'tournament' && (
            <div className="space-y-6">
              <h2 className="text-xl font-semibold text-[#FEFCEB] mb-4">
                Tournament Information
              </h2>

              {selectedTournament ? (
                <div className="space-y-4">
                  <div>
                    <label className="block text-[#B2B5AD] text-sm mb-2">
                      Tournament Name
                    </label>
                    <input
                      type="text"
                      name="name"
                      value={tournamentSettings.name}
                      onChange={handleTournamentChange}
                      className="w-full bg-[#082441] text-[#FEFCEB] rounded-lg px-4 py-3 border border-[#192634] outline-none focus:border-[#00E7FF] transition-colors"
                      placeholder="Enter tournament name"
                    />
                  </div>

                  <div>
                    <label className="block text-[#B2B5AD] text-sm mb-2">
                      Description
                    </label>
                    <textarea
                      name="description"
                      value={tournamentSettings.description}
                      onChange={handleTournamentChange}
                      rows={4}
                      className="w-full bg-[#082441] text-[#FEFCEB] rounded-lg px-4 py-3 border border-[#192634] outline-none focus:border-[#00E7FF] transition-colors resize-none"
                      placeholder="Enter tournament description"
                    />
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-[#B2B5AD] text-sm mb-2">
                        Start Date
                      </label>
                      <input
                        type="date"
                        name="startDate"
                        value={tournamentSettings.startDate}
                        onChange={handleTournamentChange}
                        className="w-full bg-[#082441] text-[#FEFCEB] rounded-lg px-4 py-3 border border-[#192634] outline-none focus:border-[#00E7FF] transition-colors"
                      />
                    </div>

                    <div>
                      <label className="block text-[#B2B5AD] text-sm mb-2">
                        End Date
                      </label>
                      <input
                        type="date"
                        name="endDate"
                        value={tournamentSettings.endDate}
                        onChange={handleTournamentChange}
                        className="w-full bg-[#082441] text-[#FEFCEB] rounded-lg px-4 py-3 border border-[#192634] outline-none focus:border-[#00E7FF] transition-colors"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-[#B2B5AD] text-sm mb-2">
                      Location
                    </label>
                    <input
                      type="text"
                      name="location"
                      value={tournamentSettings.location}
                      onChange={handleTournamentChange}
                      className="w-full bg-[#082441] text-[#FEFCEB] rounded-lg px-4 py-3 border border-[#192634] outline-none focus:border-[#00E7FF] transition-colors"
                      placeholder="Enter location"
                    />
                  </div>

                  <div>
                    <label className="block text-[#B2B5AD] text-sm mb-2">
                      Rules & Regulations
                    </label>
                    <textarea
                      name="rules"
                      value={tournamentSettings.rules}
                      onChange={handleTournamentChange}
                      rows={6}
                      className="w-full bg-[#082441] text-[#FEFCEB] rounded-lg px-4 py-3 border border-[#192634] outline-none focus:border-[#00E7FF] transition-colors resize-none"
                      placeholder="Enter tournament rules and regulations"
                    />
                  </div>

                  <div className="flex justify-end">
                    <button
                      onClick={handleSaveTournamentSettings}
                      disabled={loading}
                      className="bg-[#00E7FF] text-[#000F1F] px-6 py-3 rounded-lg font-semibold hover:bg-[#00d4e6] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {loading ? 'Saving...' : 'Save'}
                    </button>
                  </div>
                </div>
              ) : (
                <div className="text-center py-12">
                  <p className="text-[#7F8585]">No tournament selected</p>
                </div>
              )}
            </div>
          )}

          {/* Organization Settings Tab */}
          {activeTab === 'organization' && (
            <div className="space-y-6">
              <h2 className="text-xl font-semibold text-[#FEFCEB] mb-4">
                Organization Information
              </h2>

              {selectedOrganisation ? (
                <div className="space-y-4">
                  <div>
                    <label className="block text-[#B2B5AD] text-sm mb-2">
                      Organization Name
                    </label>
                    <input
                      type="text"
                      name="name"
                      value={orgSettings.name}
                      onChange={handleOrgChange}
                      className="w-full bg-[#082441] text-[#FEFCEB] rounded-lg px-4 py-3 border border-[#192634] outline-none focus:border-[#00E7FF] transition-colors"
                      placeholder="Enter organization name"
                    />
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-[#B2B5AD] text-sm mb-2">
                        Email
                      </label>
                      <input
                        type="email"
                        name="email"
                        value={orgSettings.email}
                        onChange={handleOrgChange}
                        className="w-full bg-[#082441] text-[#FEFCEB] rounded-lg px-4 py-3 border border-[#192634] outline-none focus:border-[#00E7FF] transition-colors"
                        placeholder="organization@example.com"
                      />
                    </div>

                    <div>
                      <label className="block text-[#B2B5AD] text-sm mb-2">
                        Phone
                      </label>
                      <input
                        type="tel"
                        name="phone"
                        value={orgSettings.phone}
                        onChange={handleOrgChange}
                        className="w-full bg-[#082441] text-[#FEFCEB] rounded-lg px-4 py-3 border border-[#192634] outline-none focus:border-[#00E7FF] transition-colors"
                        placeholder="+1 234 567 8900"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-[#B2B5AD] text-sm mb-2">
                      Address
                    </label>
                    <input
                      type="text"
                      name="address"
                      value={orgSettings.address}
                      onChange={handleOrgChange}
                      className="w-full bg-[#082441] text-[#FEFCEB] rounded-lg px-4 py-3 border border-[#192634] outline-none focus:border-[#00E7FF] transition-colors"
                      placeholder="Enter address"
                    />
                  </div>

                  <div>
                    <label className="block text-[#B2B5AD] text-sm mb-2">
                      Website
                    </label>
                    <input
                      type="url"
                      name="website"
                      value={orgSettings.website}
                      onChange={handleOrgChange}
                      className="w-full bg-[#082441] text-[#FEFCEB] rounded-lg px-4 py-3 border border-[#192634] outline-none focus:border-[#00E7FF] transition-colors"
                      placeholder="https://example.com"
                    />
                  </div>

                  <div className="flex justify-end">
                    <button
                      onClick={handleSaveOrgSettings}
                      disabled={loading}
                      className="bg-[#00E7FF] text-[#000F1F] px-6 py-3 rounded-lg font-semibold hover:bg-[#00d4e6] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {loading ? 'Saving...' : 'Save'}
                    </button>
                  </div>
                </div>
              ) : (
                <div className="text-center py-12">
                  <p className="text-[#7F8585]">No organization selected</p>
                </div>
              )}
            </div>
          )}

          {/* Preferences Tab */}
          {activeTab === 'preferences' && (
            <div className="space-y-6">
              <h2 className="text-xl font-semibold text-[#FEFCEB] mb-4">
                User Preferences
              </h2>

              <div className="space-y-4">
                <div className="flex items-center justify-between p-4 bg-[#082441] rounded-lg">
                  <div>
                    <p className="text-[#FEFCEB] font-medium">Push Notifications</p>
                    <p className="text-[#7F8585] text-sm">
                      Receive notifications about tournament updates
                    </p>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      name="notifications"
                      checked={preferences.notifications}
                      onChange={handlePreferenceChange}
                      className="sr-only peer"
                    />
                    <div className="w-11 h-6 bg-gray-700 rounded-full peer peer-checked:bg-[#00E7FF] peer-checked:after:translate-x-full after:content-[''] after:absolute after:top-0.5 after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all"></div>
                  </label>
                </div>

                <div className="flex items-center justify-between p-4 bg-[#082441] rounded-lg">
                  <div>
                    <p className="text-[#FEFCEB] font-medium">Email Updates</p>
                    <p className="text-[#7F8585] text-sm">
                      Receive email updates about results and standings
                    </p>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      name="emailUpdates"
                      checked={preferences.emailUpdates}
                      onChange={handlePreferenceChange}
                      className="sr-only peer"
                    />
                    <div className="w-11 h-6 bg-gray-700 rounded-full peer peer-checked:bg-[#00E7FF] peer-checked:after:translate-x-full after:content-[''] after:absolute after:top-0.5 after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all"></div>
                  </label>
                </div>

                <div className="flex items-center justify-between p-4 bg-[#082441] rounded-lg">
                  <div>
                    <p className="text-[#FEFCEB] font-medium">Dark Mode</p>
                    <p className="text-[#7F8585] text-sm">
                      Use dark theme throughout the application
                    </p>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      name="darkMode"
                      checked={preferences.darkMode}
                      onChange={handlePreferenceChange}
                      className="sr-only peer"
                    />
                    <div className="w-11 h-6 bg-gray-700 rounded-full peer peer-checked:bg-[#00E7FF] peer-checked:after:translate-x-full after:content-[''] after:absolute after:top-0.5 after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all"></div>
                  </label>
                </div>

                <div className="flex justify-end">
                  <button
                    onClick={handleSavePreferences}
                    className="bg-[#00E7FF] text-[#000F1F] px-6 py-3 rounded-lg font-semibold hover:bg-[#00d4e6] transition-colors"
                  >
                    Save Preferences
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Account Tab */}
          {activeTab === 'account' && (
            <div className="space-y-6">
              <h2 className="text-xl font-semibold text-[#FEFCEB] mb-4">
                Account Information
              </h2>

              {user ? (
                <div className="space-y-4">
                  <div className="p-4 bg-[#082441] rounded-lg">
                    <p className="text-[#7F8585] text-sm mb-1">Name</p>
                    <p className="text-[#FEFCEB] font-medium">
                      {user.firstName} {user.lastName}
                    </p>
                  </div>

                  <div className="p-4 bg-[#082441] rounded-lg">
                    <p className="text-[#7F8585] text-sm mb-1">Email</p>
                    <p className="text-[#FEFCEB] font-medium">{user.email}</p>
                  </div>

                  <div className="p-4 bg-[#082441] rounded-lg">
                    <p className="text-[#7F8585] text-sm mb-1">Phone</p>
                    <p className="text-[#FEFCEB] font-medium">{user.phone || 'Not provided'}</p>
                  </div>

                  <div className="p-4 bg-[#082441] rounded-lg">
                    <p className="text-[#7F8585] text-sm mb-1">Role</p>
                    <p className="text-[#FEFCEB] font-medium capitalize">{user.role || 'User'}</p>
                  </div>

                  <div className="pt-4 border-t border-[#192634]">
                    <button className="text-red-500 hover:text-red-400 font-medium transition-colors">
                      Change Password
                    </button>
                  </div>
                </div>
              ) : (
                <div className="text-center py-12">
                  <p className="text-[#7F8585]">User not logged in</p>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SettingsV2;
